import type { OfferLine } from "@/hooks/use-offers";
import type { OfferSection } from "@/hooks/use-offer-sections";
import {
  fmtKr,
  fmtPlain,
  fmtQty,
  exportLineDescription,
} from "@/lib/offer-budget-image-geometry";
import {
  OFFER_EXPORT_FONT_DISPLAY_CSS,
  OFFER_EXPORT_HEX,
  OFFER_EXPORT_RULE_PX,
  OFFER_EXPORT_SEMIBOLD_CSS,
} from "@/lib/offer-export-brand";

/**
 * Samme vekt som fase-linjene, som i malen. SUM skilles ut ved å ha linje
 * både over og under, ikke ved en tyngre strek (Erik 29.09).
 */
const SUM_BORDER = `${OFFER_EXPORT_RULE_PX}px solid ${OFFER_EXPORT_HEX.tableBorder}`;

/** SUM rammes inn av linje både over og under (Erik 29.09). */
const SUM_CELL = {
  paddingTop: 6,
  paddingBottom: 6,
  borderTop: SUM_BORDER,
  borderBottom: SUM_BORDER,
} as const;

interface BudgetTotals {
  overhead_amount: number;
  discount_amount: number;
  total: number;
}

interface BudgetTotalsTableProps {
  totals: BudgetTotals;
  overheadPct: number;
  discountPctText: string;
  /** Følger linjetabellens skalering, så radene flukter (VIB-2556). */
  fontSize: number;
}

/**
 * Totaler-blokk rett under siste linje (ikke festet til bunnen).
 */
export function BudgetTotalsTable({
  totals,
  overheadPct,
  discountPctText,
  fontSize,
}: BudgetTotalsTableProps) {
  const showOverhead = totals.overhead_amount > 0;
  const showDiscount = totals.discount_amount > 0;
  // Malen har ingen linje mellom siste fase og totalene — overhead og
  // rabatt flyter rett videre, og bare SUM rammes inn.
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginTop: 6,
        lineHeight: 1.55,
        fontSize,
        tableLayout: "fixed",
      }}
    >
      {/* Samme fem kolonner som PhaseTable, så beløpene flukter. */}
      <colgroup>
        <col style={{ width: "50.6%" }} />
        <col style={{ width: "12.4%" }} />
        <col style={{ width: "17.1%" }} />
        <col style={{ width: "5.7%" }} />
        <col style={{ width: "14.4%" }} />
      </colgroup>
      <tbody>
        {showOverhead && (
          <tr style={{ color: OFFER_EXPORT_HEX.tableMuted }}>
            <td style={{ paddingTop: 4, paddingBottom: 7 }}>
              + Overheadkostnad
            </td>
            <td
              style={{
                paddingTop: 4,
                paddingBottom: 7,
                textAlign: "right",
              }}
            >
              ({overheadPct} %)
            </td>
            <td style={{ }} />
            <td style={{ paddingTop: 4, paddingBottom: 7 }}>
              kr
            </td>
            <td
              style={{
                paddingTop: 4,
                paddingBottom: 7,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtPlain(totals.overhead_amount)}
            </td>
          </tr>
        )}
        {showDiscount && (
          <tr style={{ color: OFFER_EXPORT_HEX.tableMuted }}>
            <td style={{ paddingTop: 4, paddingBottom: 7 }}>
              - rabatt
            </td>
            <td
              style={{
                paddingTop: 4,
                paddingBottom: 7,
                textAlign: "right",
              }}
            >
              {discountPctText}
            </td>
            <td style={{ }} />
            <td style={{ paddingTop: 4, paddingBottom: 7 }}>
              kr
            </td>
            <td
              style={{
                paddingTop: 4,
                paddingBottom: 7,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              -{fmtPlain(totals.discount_amount)}
            </td>
          </tr>
        )}
        <tr
          style={{
            fontWeight: OFFER_EXPORT_SEMIBOLD_CSS,
            fontSize: Math.round(fontSize * 1.25),
            fontFamily: OFFER_EXPORT_FONT_DISPLAY_CSS,
          }}
        >
          <td style={SUM_CELL}>= SUM</td>
          <td style={SUM_CELL} />
          <td style={SUM_CELL} />
          <td style={SUM_CELL}>kr</td>
          <td
            style={{
              ...SUM_CELL,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtPlain(totals.total)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

interface SectionGroup {
  section: OfferSection;
  lines: OfferLine[];
  subtotal: number;
}

interface SectionTilvalgBlocksProps {
  sectionGroups: SectionGroup[];
  fontSize: number;
}

/**
 * VIB-663e: tilvalg vises som egne sub-blokker etter SUM. Hvert tilvalg
 * har eget navn, linjer og sub-total. Kunden kan plukke fra denne lista
 * uten å forvirre hoved-SUM.
 * VIB-664-fix: skalér fontSize/padding sammen med hovedtabellen så tilvalg
 * krymper konsistent når totalen er stor.
 */
export function SectionTilvalgBlocks({
  sectionGroups,
  fontSize,
}: SectionTilvalgBlocksProps) {
  if (sectionGroups.length === 0) return null;
  return (
    <div
      style={{
        marginTop: Math.max(8, fontSize),
        paddingTop: Math.max(6, Math.round(fontSize * 0.75)),
        borderTop: `1px dashed ${OFFER_EXPORT_HEX.tableMuted}`,
      }}
    >
      {sectionGroups.map((g) => (
        <div key={g.section.id} style={{ marginBottom: Math.max(8, fontSize) }}>
          <div
            style={{
              fontSize: Math.max(fontSize - 2, 8),
              fontWeight: OFFER_EXPORT_SEMIBOLD_CSS,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              marginBottom: 4,
              color: OFFER_EXPORT_HEX.tableText,
            }}
          >
            Tilvalg: {g.section.name}
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize,
              lineHeight: 1.35,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              <col />
              <col style={{ width: 50 }} />
              <col style={{ width: 36 }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <tbody>
              {g.lines.map((l) => {
                const qty = l.quantity ?? 0;
                const sectionRowPad = Math.max(2, Math.round(fontSize * 0.25));
                const tdBase = {
                  paddingTop: sectionRowPad,
                  paddingBottom: sectionRowPad,
                  lineHeight: 1.35,
                  verticalAlign: "middle" as const,
                };
                return (
                  <tr key={l.id}>
                    <td style={{ ...tdBase, paddingRight: 8 }}>
                      {exportLineDescription(l.description)}
                    </td>
                    <td
                      style={{
                        ...tdBase,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {qty > 0 ? fmtQty(qty) : ""}
                    </td>
                    <td style={{ ...tdBase, textAlign: "center" }}>
                      {qty > 0 ? l.unit ?? "stk" : ""}
                    </td>
                    <td style={{ ...tdBase, textAlign: "right" }}>
                      {fmtKr(l.amount ?? 0)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: OFFER_EXPORT_SEMIBOLD_CSS }}>
                <td
                  colSpan={3}
                  style={{
                    paddingTop: 4,
                    borderTop: `1px solid ${OFFER_EXPORT_HEX.tableBorder}`,
                  }}
                >
                  Sum {g.section.name}
                </td>
                <td
                  style={{
                    paddingTop: 4,
                    borderTop: `1px solid ${OFFER_EXPORT_HEX.tableBorder}`,
                    textAlign: "right",
                  }}
                >
                  {fmtKr(g.subtotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
